import axios from "axios";
import {mapConfig} from "./mapConfig";

const CACHE_PREFIX = 'geo:';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

// Strings the codebase has historically used to mark a failed geocode.
// Anything matching these is rejected on read so we never persist or display
// a sentinel as if it were a real address.
const INVALID_ADDRESSES = new Set(['address not found', 'unknown location']);

export const isValidAddress = (addr) => {
    if (addr === null || addr === undefined) return false;
    const s = String(addr).trim();
    if (s === '') return false;
    return !INVALID_ADDRESSES.has(s.toLowerCase());
};

const roundCoord = (val) => parseFloat(parseFloat(val).toFixed(5));

const getCacheKey = (lat, lon) => `${CACHE_PREFIX}${roundCoord(lat)},${roundCoord(lon)}`;

const getCached = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const entry = JSON.parse(raw);
        if (Date.now() - entry.ts > CACHE_TTL_MS) {
            localStorage.removeItem(key);
            return null;
        }
        return entry.address;
    } catch {
        return null;
    }
};

const setCache = (key, address) => {
    try {
        localStorage.setItem(key, JSON.stringify({ address, ts: Date.now() }));
    } catch { /* quota exceeded - ignore */ }
};

// Nominatim's usage policy allows at most 1 request/second. Serialize the actual
// network calls through a single-lane queue with a >=1.1s gap so a feed of alerts
// (Activity page) reverse-geocoding many rows never bursts the public endpoint.
// Cache HITS never enter this queue, so already-known addresses stay instant.
const MIN_GAP_MS = 1100;
let lastFetchAt = 0;
let queue = Promise.resolve();
const throttled = (fn) => {
    const run = queue.then(async () => {
        const wait = MIN_GAP_MS - (Date.now() - lastFetchAt);
        if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
        lastFetchAt = Date.now();
        return fn();
    });
    // Keep the lane alive even if a call rejects, so one failure can't stall it.
    queue = run.catch(() => {});
    return run;
};

/**
 * Reverse-geocode a {lat, lon} object via Nominatim with localStorage caching.
 * Returns the address string on success, or null on any failure. Callers must
 * guard with a truthy check before displaying or persisting. Cache misses are
 * throttled to <=1 req/sec app-wide (Nominatim policy).
 */
export const geocodeLatLng = async (location) => {
    if (!location || location['lat'] == null || location['lon'] == null) {
        return null;
    }
    if (parseFloat(location['lat']) === 0 && parseFloat(location['lon']) === 0) {
        return null;
    }

    const key = getCacheKey(location['lat'], location['lon']);
    const cached = getCached(key);
    if (cached) return cached;

    try {
        const response = await throttled(() => axios.get(mapConfig.reverseUrl, {
            headers: {
                accept: '*/*',
                'X-Requested-With': 'XMLHttpRequest',
            },
            params: {
                format: 'json',
                lat: location['lat'],
                lon: location['lon'],
            }
        }));

        const address = response.data?.display_name;
        if (!isValidAddress(address)) {
            return null;
        }
        setCache(key, address);
        return address;
    } catch (error) {
        console.error('Geocoding error:', error);
        return null;
    }
};
